import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import './StartupJobs.css';
import Footer from '../components/home/Footer';
import { formatSalaryDisplay } from '../config/salary';
import { getJobPlan, isFreeJobPlan } from '../config/jobPostingPlans';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

const previewText = (text, max = 140) => {
  const clean = String(text || '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return 'No description provided.';
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
};

const formatPostedAgo = (value, nowMs = Date.now()) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = nowMs - date.getTime();
  if (diffMs < 0) return 'Just now';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(diffMs / 3600000);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(diffMs / 86400000);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

/** How long until the relative label for this timestamp would change */
const msUntilPostedLabelChange = (value, nowMs = Date.now()) => {
  if (!value) return 60_000;
  const postedMs = new Date(value).getTime();
  if (Number.isNaN(postedMs)) return 60_000;
  const age = Math.max(0, nowMs - postedMs);
  const MINUTE = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const WEEK = 7 * DAY;
  const MONTH = 30 * DAY;

  if (age < HOUR) return Math.max(1_000, MINUTE - (age % MINUTE));
  if (age < DAY) return Math.max(1_000, HOUR - (age % HOUR));
  if (age < WEEK) return Math.max(1_000, DAY - (age % DAY));
  if (age < 5 * WEEK) return Math.max(1_000, WEEK - (age % WEEK));
  return Math.max(1_000, MONTH - (age % MONTH));
};

const MetaItem = ({ icon: Icon, children }) => {
  if (!children && children !== 0) return null;
  return (
    <span className="job-listing-meta-item">
      <Icon className="job-listing-meta-icon" aria-hidden />
      {children}
    </span>
  );
};

const StartupJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [nowTick, setNowTick] = useState(() => Date.now());
  const INITIAL_JOBS_VISIBLE = 10;

  const plans = [
    {
      name: 'Free',
      price: 'Free',
      description: 'Standard job listing to attract candidates for open roles.'
    },
    {
      name: 'Enhanced',
      price: '$29',
      description: 'Greater visibility for your hiring position across the platform.',
      highlight: true
    },
    {
      name: 'Premium',
      price: '$99',
      description: 'Maximum visibility for priority roles and key hires.'
    }
  ];

  useEffect(() => {
    const loadJobs = async () => {
      setJobsLoading(true);
      try {
        const res = await fetch(`${API_BASE}/jobs`);
        if (res.ok) {
          const data = await res.json();
          setJobs(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to load startup jobs', error);
      } finally {
        setJobsLoading(false);
      }
    };
    loadJobs();
  }, []);

  useEffect(() => {
    const tick = () => setNowTick(Date.now());

    // Refresh when tab is focused again (browsers throttle background timers)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);

    // Fallback tick every minute
    const interval = setInterval(tick, 60_000);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, []);

  // Schedule the next update right when any job's label would change
  // (minute → hour → day → week → month)
  useEffect(() => {
    if (!jobs.length) return undefined;
    const now = Date.now();
    const nextMs = jobs.reduce((soonest, job) => {
      const wait = msUntilPostedLabelChange(job.postedAt || job.createdAt, now);
      return Math.min(soonest, wait);
    }, 60_000);
    const timer = setTimeout(() => setNowTick(Date.now()), Math.min(nextMs, 60_000));
    return () => clearTimeout(timer);
  }, [jobs, nowTick]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setSearchQuery(String(searchInput || '').trim());
    setShowAllJobs(false);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setShowAllJobs(false);
  };

  const filteredJobs = (() => {
    const q = searchQuery.toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) => {
      const title = String(job.title || '').toLowerCase();
      const skills = Array.isArray(job.skills)
        ? job.skills.map((s) => String(s || '').toLowerCase())
        : [];
      return title.includes(q) || skills.some((skill) => skill.includes(q));
    });
  })();

  return (
    <div className="startup-staffing">
      <div className="staffing-container">
        <div className="staffing-header">
          <p className="jobs-eyebrow">Jobs</p>
          <h1>Startup Jobs & Hiring</h1>
          <p>
            Post open roles, attract qualified candidates, and grow your team.
            CofounderHubs helps startups hire efficiently — with optional recruitment support when you need it.
          </p>
          <div className="jobs-hero-actions">
            <Link to="/post-job" className="jobs-btn jobs-btn-primary">Post a Job</Link>
            <Link to="/search" className="jobs-btn jobs-btn-secondary">Find Talent</Link>
          </div>
        </div>

        <div className="jobs-listings-section">
          <form className="jobs-search-bar" onSubmit={handleSearch}>
            <input
              type="search"
              className="jobs-search-input"
              placeholder="Search by job title or skill (e.g. React, Frontend)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search jobs by title or skill"
            />
            <button type="submit" className="jobs-btn jobs-btn-primary">
              Search
            </button>
            {searchQuery ? (
              <button type="button" className="jobs-btn jobs-btn-secondary" onClick={clearSearch}>
                Clear
              </button>
            ) : null}
          </form>

          <h2>Published jobs</h2>
          <p className="jobs-plans-lead">
            {searchQuery
              ? `Showing results for “${searchQuery}”`
              : 'Browse open roles from startups hiring on CofounderHubs.'}
          </p>

          {jobsLoading && <p className="jobs-plans-lead">Loading jobs...</p>}

          {!jobsLoading && jobs.length === 0 && (
            <p className="jobs-plans-lead">No published jobs yet. Check back soon, or post a job.</p>
          )}

          {!jobsLoading && jobs.length > 0 && filteredJobs.length === 0 && (
            <p className="jobs-plans-lead">
              No jobs match “{searchQuery}”. Try another title or skill keyword.
            </p>
          )}

          {!jobsLoading && filteredJobs.length > 0 && (
            <>
              <div className="jobs-listing-grid">
                {(showAllJobs ? filteredJobs : filteredJobs.slice(0, INITIAL_JOBS_VISIBLE)).map((job) => {
                const skills = Array.isArray(job.skills) ? job.skills.filter(Boolean).slice(0, 10) : [];
                const salaryLabel = formatSalaryDisplay(job);
                const postedAgo = formatPostedAgo(job.postedAt || job.createdAt, nowTick);
                const viewCount = Number(job.viewCount) || 0;
                const applicationCount = Number(job.applicationCount) || 0;
                const workMode =
                  String(job.location || '')
                    .toLowerCase()
                    .includes('remote')
                    ? 'Remote'
                    : null;
                return (
                  <article key={job.id} className="job-listing-card">
                    <div className="job-listing-card-top">
                      <div className="job-listing-logo">
                        {job.companyLogoUrl ? (
                          <img
                            src={resolveMediaUrl(job.companyLogoUrl)}
                            alt={job.companyName ? `${job.companyName} logo` : `${job.title} logo`}
                          />
                        ) : (
                          <span className="job-listing-logo-fallback">
                            {(job.companyName || job.title || 'J').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="job-listing-main">
                        <div className="job-listing-body">
                          <div className="job-listing-heading">
                            <div className="job-listing-title-row">
                              <h3>{job.title}</h3>
                              {(() => {
                                const planId = job.plan || 'basic';
                                const plan = getJobPlan(planId);
                                const label = isFreeJobPlan(planId) ? 'Free' : plan.name.replace(' Job Posting', '');
                                return (
                                  <span
                                    className={`job-listing-plan-badge job-listing-plan-${planId || 'basic'}`}
                                  >
                                    {label}
                                  </span>
                                );
                              })()}
                            </div>
                            <p className="job-listing-company">
                              {job.companyName || 'Startup hiring on CofounderHubs'}
                            </p>
                            <p className="job-listing-desc">{previewText(job.description, 160)}</p>
                          </div>

                          <div className="job-listing-meta">
                            {workMode ? (
                              <MetaItem icon={AccessTimeOutlinedIcon}>{workMode}</MetaItem>
                            ) : null}
                            {job.location && String(job.location).toLowerCase() !== 'remote' ? (
                              <MetaItem icon={PlaceOutlinedIcon}>{job.location}</MetaItem>
                            ) : null}
                            <MetaItem icon={BadgeOutlinedIcon}>
                              {job.type || 'Full-time'}
                            </MetaItem>
                            {job.department ? (
                              <MetaItem icon={WorkOutlineOutlinedIcon}>{job.department}</MetaItem>
                            ) : null}
                            {salaryLabel ? (
                              <MetaItem icon={SellOutlinedIcon}>{salaryLabel}</MetaItem>
                            ) : null}
                          </div>

                          {skills.length > 0 ? (
                            <div className="job-listing-skills">
                              {skills.map((skill) => (
                                <span key={skill} className="job-listing-skill">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="job-listing-aside">
                          <div className="job-listing-stats">
                            {postedAgo ? (
                              <p className="job-listing-stat">Posted: {postedAgo}</p>
                            ) : null}
                            <p className="job-listing-stat">
                              <VisibilityOutlinedIcon className="job-listing-stat-icon" aria-hidden />
                              Views: {viewCount.toLocaleString()}
                            </p>
                            <p className="job-listing-stat">
                              <DescriptionOutlinedIcon className="job-listing-stat-icon" aria-hidden />
                              Applied: {applicationCount.toLocaleString()}
                            </p>
                          </div>
                          <div className="job-listing-actions">
                            <Link to={`/careers/${encodeURIComponent(job.id)}`} className="jobs-btn jobs-btn-primary">
                              View role
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
              </div>
              {filteredJobs.length > INITIAL_JOBS_VISIBLE ? (
                <div className="jobs-view-all">
                  <button
                    type="button"
                    className="jobs-btn jobs-btn-secondary"
                    onClick={() => setShowAllJobs((prev) => !prev)}
                  >
                    {showAllJobs
                      ? 'Show less'
                      : `View all (${filteredJobs.length})`}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="jobs-plans-section">
          <h2>Job posting plans</h2>
          <p className="jobs-plans-lead">
            Companies can post hiring positions with clear, transparent pricing.
          </p>
          <div className="jobs-plans-grid">
            {plans.map((plan) => (
              <article
                key={plan.name}
                className={`jobs-plan-card${plan.highlight ? ' jobs-plan-card-highlight' : ''}`}
              >
                {plan.highlight && <span className="jobs-plan-badge">Popular</span>}
                <h3>{plan.name}</h3>
                <div className="jobs-plan-price">{plan.price}</div>
                <p>{plan.description}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="coming-soon-section jobs-support-section">
          <h2>Recruitment support</h2>
          <p className="coming-soon-description">
            Need help finding suitable talent? CofounderHubs can assist with identifying,
            screening, and connecting candidates for your open roles. Contact us to discuss
            recruitment partner support for your company.
          </p>
          <div className="jobs-hero-actions" style={{ marginTop: '1.5rem' }}>
            <Link to="/contact" className="jobs-btn jobs-btn-secondary">Contact Us</Link>
            <Link to="/pricing" className="jobs-btn jobs-btn-secondary">View Pricing</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default StartupJobs;
